import { FC, Fragment, useEffect } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { ChevronDownIcon, SparklesIcon } from '@heroicons/react/outline';

import {
  PatientNameDataFragment,
  ProgramTagDataFragment,
  ProgramTagWithCountDataFragment,
} from '../../../generated/graphql';

import Avatar from '../../components/Avatar';
import Tag from '../../svgs/Tag';
import { pluralize } from '../../lib/copy';
import PatientPill from './PatientPill';
import ProgramTagPill from './ProgramTagPill';
import {
  PatientSelectionEntry,
  useRecipientSelection,
} from './hooks/useRecipientSelection';

type PatientsOrTags = (
  | PatientNameDataFragment
  | ProgramTagWithCountDataFragment
  | ProgramTagDataFragment
)[];

export type RecipientSelectorProps = {
  value: PatientsOrTags;
  onChange: (value: PatientsOrTags) => void;
  patientsList: PatientNameDataFragment[];
  providerProgramTags: ProgramTagWithCountDataFragment[];
  onPatientSelectionsChange?: (
    patientSelections: Record<string, PatientSelectionEntry>,
  ) => void;
  className?: string;
};

const RecipientSelector: FC<RecipientSelectorProps> = ({
  value,
  onChange,
  patientsList,
  providerProgramTags,
  onPatientSelectionsChange,
  className,
}) => {
  const {
    tagSelections,
    tagOrder,
    patientSelections,
    patientOrder,
    patientOrTagQuery,
    setPatientOrTagQuery,
    filteredPatientsOrTags,
    cloneSelectionState,
    removePatientFromState,
    removeTagFromState,
    buildRecipientsValue,
    setRecipients,
    setTagSelections,
    setTagOrder,
    setPatientSelections,
    setPatientOrder,
  } = useRecipientSelection({
    watchPatientsOrTags: value,
    providerProgramTags,
    patientsList,
  });

  useEffect(() => {
    onPatientSelectionsChange?.(patientSelections);
  }, [onPatientSelectionsChange, patientSelections]);

  const displayRecipients = buildRecipientsValue(
    tagSelections,
    tagOrder,
    patientSelections,
    patientOrder,
  );

  const hasMultipleRecipients =
    displayRecipients.length > 1 ||
    displayRecipients[0]?.__typename === 'ProgramTag';

  const isPatientName = (
    item: PatientsOrTags[number],
  ): item is PatientNameDataFragment => item?.__typename === 'PatientName';

  const isProgramTag = (
    item: PatientsOrTags[number],
  ): item is ProgramTagWithCountDataFragment =>
    item?.__typename === 'ProgramTag';

  const applySelectionState = (
    state: ReturnType<typeof cloneSelectionState>,
    options: { resetQuery?: boolean } = {},
  ) => {
    setTagSelections(state.tagSelections);
    setTagOrder(state.tagOrder);
    setPatientSelections(state.patientSelections);
    setPatientOrder(state.patientOrder);
    const nextValue = buildRecipientsValue(
      state.tagSelections,
      state.tagOrder,
      state.patientSelections,
      state.patientOrder,
    );
    onChange(nextValue);
    if (options.resetQuery) {
      setPatientOrTagQuery('');
    }
  };

  const handleRemovePatient = (patient: PatientNameDataFragment) => {
    const nextState = cloneSelectionState();
    removePatientFromState(nextState, patient.programInstanceId);
    applySelectionState(nextState);
  };

  const handleRemoveTag = (tag: ProgramTagWithCountDataFragment) => {
    const nextState = cloneSelectionState();
    removeTagFromState(nextState, tag.id);
    applySelectionState(nextState);
  };

  const handleToggleTagPatientSelection = (
    programTag: ProgramTagWithCountDataFragment,
    patient: PatientNameDataFragment,
    nextSelected: boolean,
  ) => {
    const nextState = cloneSelectionState();
    const tagEntry = nextState.tagSelections[programTag.id];
    if (!tagEntry) {
      return;
    }

    tagEntry.patients[patient.programInstanceId] = {
      patient,
      selected: nextSelected,
    };

    const patientEntry = nextState.patientSelections[
      patient.programInstanceId
    ] ?? {
      patient,
      manual: false,
      selectedTagIds: [],
    };

    const selectedTagIdsSet = new Set(patientEntry.selectedTagIds);

    if (nextSelected) {
      selectedTagIdsSet.add(programTag.id);
    } else {
      selectedTagIdsSet.delete(programTag.id);
    }

    patientEntry.selectedTagIds = Array.from(selectedTagIdsSet);

    if (!patientEntry.manual && patientEntry.selectedTagIds.length === 0) {
      delete nextState.patientSelections[patient.programInstanceId];
      nextState.patientOrder = nextState.patientOrder.filter(
        (id) => id !== patient.programInstanceId,
      );
    } else {
      nextState.patientSelections[patient.programInstanceId] = patientEntry;
      if (!nextState.patientOrder.includes(patient.programInstanceId)) {
        nextState.patientOrder.push(patient.programInstanceId);
      }
    }

    const hasSelected = Object.values(tagEntry.patients).some(
      (entry) => entry.selected,
    );

    if (!hasSelected) {
      delete nextState.tagSelections[programTag.id];
      nextState.tagOrder = nextState.tagOrder.filter(
        (id) => id !== programTag.id,
      );
    } else {
      nextState.tagSelections[programTag.id] = tagEntry;
    }

    applySelectionState(nextState);
  };

  const handleComboboxChange = async (nextSelection: PatientsOrTags) => {
    const nextValue = await setRecipients(nextSelection);
    onChange(nextValue);
    setPatientOrTagQuery('');
  };

  return (
    <div className={className}>
      {Boolean(displayRecipients.length) && (
        <>
          <ul className="mb-4 flex flex-row flex-wrap gap-2">
            {displayRecipients.map((recipient) => {
              if (isPatientName(recipient)) {
                return (
                  <PatientPill
                    key={recipient.programInstanceId}
                    patient={recipient}
                    onDelete={() => handleRemovePatient(recipient)}
                  />
                );
              }
              if (isProgramTag(recipient)) {
                const tagEntry = tagSelections[recipient.id];
                const tagPatients = tagEntry
                  ? Object.values(tagEntry.patients)
                      .map(({ patient, selected }) => ({
                        patient,
                        selected,
                      }))
                      .sort((a, b) =>
                        `${a.patient.firstName} ${a.patient.lastName}`
                          .toLowerCase()
                          .localeCompare(
                            `${b.patient.firstName} ${b.patient.lastName}`.toLowerCase(),
                          ),
                      )
                  : [];
                return (
                  <ProgramTagPill
                    key={recipient.id}
                    programTag={recipient}
                    patients={tagPatients}
                    onDelete={() => handleRemoveTag(recipient)}
                    onTogglePatient={(programTag, patient, selected) =>
                      handleToggleTagPatientSelection(
                        programTag,
                        patient,
                        selected,
                      )
                    }
                  />
                );
              }
              return null;
            })}
          </ul>
          {hasMultipleRecipients && (
            <div className="mb-4 flex flex-row items-center gap-x-2 text-neutral-125">
              <SparklesIcon className="h-6 min-w-[24px]" />
              <div className="text-caption text-neutral-200">
                Your mail will be delivered to these recipients separately.
              </div>
            </div>
          )}
        </>
      )}

      <Combobox
        as="div"
        className="relative mb-8 w-full text-body"
        value={displayRecipients}
        onChange={(selection) => {
          void handleComboboxChange(selection as PatientsOrTags);
        }}
        immediate
        multiple
      >
        <Combobox.Input
          className={classNames(
            'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
            'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-secondary-150 shadow-sm sm:text-caption',
            'border-0 placeholder:text-neutral-110 focus:border-secondary-100 focus:outline-none focus:ring-2 focus:ring-secondary-50',
            'selection:bg-secondary-20',
          )}
          onChange={(event) => setPatientOrTagQuery(event.target.value)}
          placeholder="Search recipients or tags"
          displayValue={() => patientOrTagQuery}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronDownIcon
            className="h-5 w-5 text-neutral-125"
            aria-hidden="true"
          />
        </Combobox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setPatientOrTagQuery('')}
        >
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
            {filteredPatientsOrTags.map((patientOrTag) => {
              if (patientOrTag.__typename === 'PatientName') {
                return (
                  <Combobox.Option
                    key={patientOrTag.programInstanceId}
                    value={patientOrTag}
                  >
                    {({ active, selected }) => {
                      const patientName = `${patientOrTag?.firstName} ${patientOrTag?.lastName}`;
                      return (
                        <div
                          className={classNames(
                            'relative flex cursor-pointer select-none flex-row items-center truncate py-3 px-5 text-green-150 hover:bg-neutral-25',
                            {
                              'bg-neutral-25': selected || active,
                            },
                          )}
                        >
                          <Avatar
                            size="small"
                            name={patientName}
                            imageUrl={patientOrTag?.profileImageMedia?.url}
                            className="mr-3"
                          />
                          {patientName}
                        </div>
                      );
                    }}
                  </Combobox.Option>
                );
              }
              if (patientOrTag.__typename === 'ProgramTag') {
                const disabled = !Boolean(patientOrTag.programCount);
                return (
                  <Combobox.Option
                    key={`tagOption_${patientOrTag.id}`}
                    value={patientOrTag}
                    disabled={disabled}
                  >
                    {({ active, selected }) => (
                      <div
                        className={classNames(
                          'relative flex select-none flex-row truncate py-3 px-5 text-green-150 hover:bg-neutral-25',
                          {
                            'bg-neutral-25': selected || active,
                          },
                          disabled
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer',
                        )}
                      >
                        <div className="flex w-full items-center space-x-3">
                          <Tag className="h-6 min-w-[24px]" />
                          <div className="flex-grow truncate text-caption">
                            {patientOrTag.name}{' '}
                            {`(${pluralize(
                              patientOrTag.programCount,
                              'client',
                              'clients',
                            )})`}
                          </div>
                          <div className="truncate text-small-caption text-neutral-100">
                            Mail will deliver separately.
                          </div>
                        </div>
                      </div>
                    )}
                  </Combobox.Option>
                );
              }
              return null;
            })}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </div>
  );
};

export default RecipientSelector;
